// Per-carrier shipping breakdown table.
//
// Used by:
//   • Admin Overview dashboard — current month-to-date (no period prop)
//   • Admin History page       — selected historical month
//
// Data source: backend /admin/shipping-by-carrier (already aggregates
// shipping_cost, carrier_cost, attributed postage discrepancies, order
// counts per carrier, with the same date predicate as list_clients so
// MTD reconciles).

import { Truck } from 'lucide-react'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}

export default function ShippingByCourierTable({ rows, isLoading, periodLabel }) {
  const safeRows      = rows || []
  const totalShipping = safeRows.reduce((s, r) => s + (r.total_shipping || 0), 0)
  const totalOrders   = safeRows.reduce((s, r) => s + (r.order_count || 0), 0)
  const totalDiscrep  = safeRows.reduce((s, r) => s + (r.discrepancies || 0), 0)
  const hasDiscrepancies = totalDiscrep !== 0

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Truck size={16} className="text-slate-500" />
        <h3 className="font-semibold text-slate-800">Shipping Cost by Courier — {periodLabel}</h3>
      </div>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">Carrier</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Orders</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Shipping</th>
                {hasDiscrepancies && (
                  <th
                    className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider"
                    title="Postage discrepancies attributed to this carrier via the linked order"
                  >
                    Discrepancies
                  </th>
                )}
                <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Avg / Order</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: hasDiscrepancies ? 7 : 6 }).map((_, j) => (
                      <td key={j} className="px-3 py-2"><div className="skeleton h-3 rounded w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : safeRows.length === 0 ? (
                <tr>
                  <td colSpan={hasDiscrepancies ? 7 : 6} className="py-8 text-center">
                    <Truck size={28} className="mx-auto text-slate-300 mb-1" />
                    <p className="text-slate-400 text-sm">No shipped orders in {periodLabel}</p>
                  </td>
                </tr>
              ) : safeRows.map((r) => {
                const baseShipping = (r.total_shipping || 0) - (r.discrepancies || 0)
                const share = totalShipping > 0 ? (r.total_shipping / totalShipping) * 100 : 0
                const avg   = r.order_count > 0 ? r.total_shipping / r.order_count : 0
                return (
                  <tr key={r.carrier} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{r.carrier}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.order_count.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(baseShipping)}</td>
                    {hasDiscrepancies && (
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                        {r.discrepancies ? formatCurrency(r.discrepancies) : <span className="text-slate-300">—</span>}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{formatCurrency(r.total_shipping)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(avg)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-blue-400 rounded-full" style={{ width: `${share.toFixed(1)}%` }} />
                        </div>
                        <span className="w-10 text-right">{share.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {safeRows.length > 0 && (
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr>
                  <td className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">
                    Total ({safeRows.length} carrier{safeRows.length === 1 ? '' : 's'})
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{totalOrders.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{formatCurrency(totalShipping - totalDiscrep)}</td>
                  {hasDiscrepancies && (
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-700">{formatCurrency(totalDiscrep)}</td>
                  )}
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800">{formatCurrency(totalShipping)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                    {formatCurrency(totalOrders > 0 ? totalShipping / totalOrders : 0)}
                  </td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
